import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

type ServerMode = 'TEST' | 'PROD' | 'loading' | 'error';

/**
 * Shows whether revenue emails use the test inbox or real invoicing contacts.
 * Reads `EMAIL_SENDING_MODE` via the `email_sending_mode` Edge Function (same as `send_campaign_email`).
 */
export function EmailTestModeBadge() {
  const [mode, setMode] = useState<ServerMode>('loading');

  const fetchMode = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke<{ email_sending_mode?: string }>(
        'email_sending_mode',
        { method: 'GET' }
      );
      if (error) {
        console.warn('email_sending_mode invoke:', error.message ?? error);
        setMode('error');
        return;
      }
      const raw = data?.email_sending_mode?.toUpperCase();
      setMode(raw === 'TEST' ? 'TEST' : 'PROD');
    } catch (e) {
      console.warn('email_sending_mode:', e);
      setMode('error');
    }
  }, []);

  useEffect(() => {
    void fetchMode();
  }, [fetchMode]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') void fetchMode();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchMode]);

  if (mode === 'loading') {
    return (
      <span
        className="shrink-0 rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
        title="Checking email mode…"
      >
        …
      </span>
    );
  }

  if (mode === 'error') {
    return (
      <span
        className="shrink-0 rounded border border-amber-300/80 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900"
        title="The email_sending_mode Edge Function is missing or failed (often not deployed). Deploy it: supabase functions deploy email_sending_mode — then reload. EMAIL_SENDING_MODE is read at runtime from Supabase Edge Function secrets."
      >
        Mode unknown
      </span>
    );
  }

  if (mode === 'PROD') {
    return (
      <span
        className="shrink-0 rounded border border-rose-300/90 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-900"
        title="EMAIL_SENDING_MODE is not TEST on Edge Functions: launch/reminder/confirmation emails go to real hotel invoicing contacts. Set secret EMAIL_SENDING_MODE=TEST (and EMAIL_TEST_RECIPIENT) in Supabase to redirect sends."
      >
        Live emails
      </span>
    );
  }

  return (
    <span
      className="shrink-0 rounded border border-amber-300/80 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900"
      title="EMAIL_SENDING_MODE=TEST on Edge Functions: outbound revenue emails are redirected to EMAIL_TEST_RECIPIENT."
    >
      TEST MODE
    </span>
  );
}
